import { ProfileCompletionCard } from "../shell/CompleteProfilePrompt";
import {
  BlobCtaButton,
  CardGlass,
  ErrorPage,
  GlassChip,
  Progress,
  Skeleton,
  Typography,
} from "@cupcodev/ui";
import {ServiceIdentity,PackageLabel} from "../../ui/ServiceIdentity";
import {Link,useNavigate} from "react-router-dom";
import { NaveIcon, RowIcon, RowLink, RowList } from "../../ui/NavePage";
import { useEffect, useState, type ReactNode } from "react";
import {
  mapHomeLoadError,
  type HomeLoadErrorKind,
} from "../../application/foundation/home-load-error";
import type { CustomerHomeReadModel } from "../../read-models/home/customer-home-read-model";
import { shouldRenderHomeSection } from "../../composition/home/home-section-policy";
import { SectionBoundary } from "../../ui/SectionBoundary";
import { useI18n } from "../i18n/I18nProvider";
import { useScenario } from "../scenario/ScenarioProvider";
import { telemetry } from "../telemetry/telemetry";

type HomeState =
  | Readonly<{
      kind: "loading";
      loadKey: string;
    }>
  | Readonly<{
      kind: "ready";
      loadKey: string;
      home: CustomerHomeReadModel;
    }>
  | Readonly<{
      kind: "error";
      loadKey: string;
      error: HomeLoadErrorKind;
    }>;

export const HomePage = () => {
  const { runtime, activeOrganizationId } = useScenario();
  const navigate=useNavigate();

  const {
    t,
    tp,
    formatCurrency,
    formatDate,
    formatQuantity,
    formatMonth,
  } = useI18n();

  const loadKey = `${runtime.scenario.id}:${activeOrganizationId.value}`;

  const [state, setState] = useState<HomeState>(() => ({
    kind: "loading",
    loadKey,
  }));

  useEffect(() => {
    let cancelled = false;

    setState({
      kind: "loading",
      loadKey,
    });

    void runtime.home.get().then((result) => {
      if (cancelled) {
        return;
      }

      if (result.ok) {
        setState({
          kind: "ready",
          loadKey,
          home: result.value,
        });

        telemetry.emit({
          name: "readModel.loaded",
          outcome:
            result.value.meta.state === "PARTIAL"
              ? "partial"
              : "success",
        });
      } else {
        setState({
          kind: "error",
          loadKey,
          error: mapHomeLoadError(result.error.code),
        });

        telemetry.emit({
          name: "readModel.loaded",
          outcome: "failure",
          attributes: {
            code: result.error.code,
          },
        });
      }
    });

    return () => {
      cancelled = true;
    };
  }, [runtime, loadKey]);

  if (
    state.loadKey !== loadKey ||
    state.kind === "loading"
  ) {
    return (
      <div
        className="nave-loading-grid"
        aria-live="polite"
        aria-busy="true"
      >
        <span className="sr-only">
          {t("state.loading")}
        </span>
        <Skeleton variant="rounded" className="nave-skeleton nave-skeleton-hero" />
        <Skeleton variant="rounded" className="nave-skeleton nave-skeleton-pulse" />
        <Skeleton variant="rounded" className="nave-skeleton nave-skeleton-pulse" />
        <Skeleton variant="rounded" className="nave-skeleton nave-skeleton-pulse" />
        <Skeleton variant="rounded" className="nave-skeleton nave-skeleton-pulse" />
        <Skeleton variant="rounded" className="nave-skeleton" />
        <Skeleton variant="rounded" className="nave-skeleton" />
      </div>
    );
  }

  if (state.kind === "error") {
    const keys = errorKeys(state.error);

    return (
      <ErrorPage
        code={errorCode(state.error)}
        title={t(keys.title)}
        description={t(keys.body)}
      />
    );
  }

  const home = state.home;
  const attentionItems = home.needsYourAttention.data?.items ?? [];
  const projects = home.inProgress.data?.projects ?? [];
  const services = home.inProgress.data?.services ?? [];
  const finance = home.financialSummary.data;
  const support = home.supportSummary.data;
  const usable = (sectionState: string): boolean => sectionState !== "UNAVAILABLE";

  const scrollToAttention = (): void => {
    document.getElementById("home-attention")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="home-page">
      <section
        className="home-hero"
        aria-labelledby="home-title"
      >
        <div className="home-hero-copy">
          <Typography
            as="h1"
            id="home-title"
            variant="display-md"
            className="home-title"
          >
            {t("home.greeting")}
          </Typography>

          <Typography
            as="p"
            variant="body-md"
            tone="muted"
            className="home-subtitle"
          >
            {t("home.subtitle")}
          </Typography>
        </div>

        <div className="home-hero-side">
          <span className="home-date">{formatDate(new Date().toISOString())}</span>
          <GlassChip
            variant={healthVariant(home.meta.state)}
            className="health-chip"
          >
            {t(`home.health.${home.meta.state}`)}
          </GlassChip>
        </div>
      </section>

      {home.meta.state === "PARTIAL" ? (
        <div className="partial-banner" role="status">
          <NaveIcon name="lucide:info" />
          {t("state.partial")}
        </div>
      ) : null}

      {home.meta.state === "STALE" ? (
        <div className="partial-banner" role="status">
          <NaveIcon name="lucide:clock-alert" />
          {t("state.stale")}
        </div>
      ) : null}

      {home.meta.state === "UNAVAILABLE" ? (
        <div className="partial-banner" role="status">
          <NaveIcon name="lucide:cloud-off" />
          {t("state.unavailable")}
        </div>
      ) : null}

      <ProfileCompletionCard />

      <nav className="home-pulse" aria-label={t("home.pulse.label")}>
        {usable(home.needsYourAttention.state) ? (
          <button
            type="button"
            className={attentionItems.length > 0 ? "home-pulse-item home-pulse-alert" : "home-pulse-item"}
            onClick={scrollToAttention}
            disabled={attentionItems.length === 0}
          >
            <RowIcon name="lucide:bell-ring" tone={attentionItems.length > 0 ? "alert" : "neutral"} />
            <span className="home-pulse-copy">
              <span className="home-pulse-value">{attentionItems.length}</span>
              <span className="home-pulse-label">{t("home.pulse.actions")}</span>
            </span>
            <NaveIcon name="lucide:arrow-down" className="nave-row-chevron" />
          </button>
        ) : null}
        {usable(home.inProgress.state) ? (
          <Link className="home-pulse-item" to="/projects">
            <RowIcon name="lucide:rocket" tone="accent" />
            <span className="home-pulse-copy">
              <span className="home-pulse-value">{projects.length + services.length}</span>
              <span className="home-pulse-label">{t("home.pulse.inProgress")}</span>
            </span>
            <NaveIcon name="lucide:chevron-right" className="nave-row-chevron" />
          </Link>
        ) : null}
        {usable(home.financialSummary.state) && finance !== undefined && finance.financialAccount !== "NO_FINANCIAL_ACCOUNT" ? (
          <Link className={finance.overdueCount > 0 ? "home-pulse-item home-pulse-alert" : "home-pulse-item"} to="/billing">
            <RowIcon name="lucide:receipt-text" tone={finance.overdueCount > 0 ? "alert" : "neutral"} />
            <span className="home-pulse-copy">
              <span className="home-pulse-value">{finance.openCount}</span>
              <span className="home-pulse-label">{finance.overdueCount > 0 ? tp("home.pulse.overdue", finance.overdueCount) : t("home.pulse.openInvoices")}</span>
            </span>
            <NaveIcon name="lucide:chevron-right" className="nave-row-chevron" />
          </Link>
        ) : null}
        {usable(home.supportSummary.state) ? (
          <Link className="home-pulse-item" to="/support">
            <RowIcon name="lucide:life-buoy" />
            <span className="home-pulse-copy">
              <span className="home-pulse-value">{support?.activeCount ?? 0}</span>
              <span className="home-pulse-label">{(support?.waitingClientCount ?? 0) > 0 ? tp("home.pulse.waitingYou", support?.waitingClientCount ?? 0) : t("home.pulse.tickets")}</span>
            </span>
            <NaveIcon name="lucide:chevron-right" className="nave-row-chevron" />
          </Link>
        ) : null}
      </nav>

      <div className="home-grid">
        {shouldRenderHomeSection(home.needsYourAttention) ? (
          <SectionBoundary
            id="home-attention"
            icon="lucide:bell-ring"
            count={attentionItems.length}
            title={t("home.attention.title")}
            state={home.needsYourAttention.state}
            emptyKey="home.attention.empty"
            className="home-span-12"
          >
            <ul className="attention-list nave-row-list">
              {attentionItems.map((item) => {
                const body = (cta?: string) => (
                  <>
                    <span className="attention-item-top">
                      <GlassChip variant="purple">
                        {t(item.titleKey)}
                      </GlassChip>
                      {item.priority === "HIGH" || item.priority === "CRITICAL" ? (
                        <Typography
                          as="span"
                          variant="caption"
                          tone="muted"
                          className={`priority-${item.priority}`}
                        >
                          {t(`priority.${item.priority}`)}
                        </Typography>
                      ) : null}
                    </span>
                    <span className="attention-item-title">{typeof item.customerPayload.title === "string" && item.customerPayload.title ? item.customerPayload.title : t(item.titleKey)}</span>
                    <span className="attention-item-foot">
                      {item.dueAt !== undefined ? (
                        <span className="nave-meta-item">
                          <NaveIcon name="lucide:calendar-clock" />
                          {t("billing.due")}: {formatDate(item.dueAt)}
                        </span>
                      ) : <span />}
                      {cta !== undefined ? (
                        <span className="attention-item-cta">
                          {cta}
                          <NaveIcon name="lucide:arrow-right" />
                        </span>
                      ) : null}
                    </span>
                  </>
                );
                return (
                  <li key={item.id}>
                    {item.target?.type === "INTERNAL_ROUTE" && item.target.path.startsWith("/") ? (
                      <AttentionLink href={item.target.path}>{body(t(attentionCta(item.target.path)))}</AttentionLink>
                    ) : item.target?.type === "APPROVAL" ? (
                      <AttentionLink href={`/approvals/${item.target.approvalId}`}>{body(t("approvals.view"))}</AttentionLink>
                    ) : item.target?.type === "INVOICE" ? (
                      <AttentionLink href={`/billing/invoices/${item.target.invoiceId}`}>{body(t("billing.invoice.view"))}</AttentionLink>
                    ) : item.target?.type === "SUPPORT_TICKET" ? (
                      <AttentionLink href={`/support/tickets/${item.target.ticketId}`}>{body(t("support.ticket.view"))}</AttentionLink>
                    ) : item.target?.type === "REQUEST" ? (
                      <AttentionLink href={`/requests/${item.target.requestId}`}>{body(t("collaboration.requests.view"))}</AttentionLink>
                    ) : (
                      <div className="attention-item">{body()}</div>
                    )}
                  </li>
                );
              })}
            </ul>
          </SectionBoundary>
        ) : null}

        {shouldRenderHomeSection(home.inProgress) ? (
          <SectionBoundary
            icon="lucide:rocket"
            count={projects.length + services.length}
            title={t("home.progress.title")}
            state={home.inProgress.state}
            emptyKey="home.progress.empty"
            className="home-span-8"
            viewAll={{ to: "/projects", label: t("common.viewAll") }}
          >
            <RowList label={t("home.progress.title")}>
              {projects.map((project) => (
                <li key={project.projectId}>
                  <RowLink
                    href={`/projects/${encodeURIComponent(project.projectId)}`}
                    leading={<RowIcon name="lucide:folder-kanban" tone="accent" />}
                    title={<PackageLabel text={project.name} />}
                    subtitle={project.currentStageLabelKey !== undefined ? t(project.currentStageLabelKey) : undefined}
                    meta={project.nextMilestoneLabel !== undefined ? (
                      <span className="nave-meta-item">
                        <NaveIcon name="lucide:flag" />
                        {project.nextMilestoneLabel}
                        {project.nextMilestoneDate !== undefined ? ` · ${formatDate(project.nextMilestoneDate)}` : ""}
                      </span>
                    ) : undefined}
                    trailing={
                      <GlassChip variant={project.needsCustomerAction ? "purple" : "neutral"}>
                        {projectStatus(project.customerStatus, t)}
                      </GlassChip>
                    }
                  />
                </li>
              ))}
              {services.map((service) => (
                <li key={service.instanceId || service.serviceId}>
                  <RowLink
                    href={`/services/${encodeURIComponent(service.instanceId || service.serviceId)}`}
                    leading={service.logoUrl ? <img src={service.logoUrl} alt="" /> : <RowIcon name="lucide:layers" />}
                    title={<PackageLabel text={service.name} />}
                    subtitle={service.planName && service.planName !== service.name ? <PackageLabel text={service.planName} /> : service.description}
                    meta={service.startsAt ? (
                      <span className="nave-meta-item">
                        <NaveIcon name="lucide:calendar" />
                        {t("contracts.startsAt")}: {formatDate(service.startsAt)}
                      </span>
                    ) : undefined}
                    trailing={<GlassChip variant={service.customerStatus === "ACTIVE" ? "success" : "neutral"}>{t(`services.status.${service.customerStatus}`)}</GlassChip>}
                  />
                </li>
              ))}
            </RowList>
          </SectionBoundary>
        ) : null}

        {shouldRenderHomeSection(home.financialSummary) ? (
          <SectionBoundary
            icon="lucide:wallet"
            title={t("home.billing.title")}
            state={home.financialSummary.state}
            emptyKey="home.billing.empty"
            className="home-span-4"
            viewAll={{ to: "/billing", label: t("common.viewAll") }}
          >
            {finance?.financialAccount === "NO_FINANCIAL_ACCOUNT" ? (
              <div className="empty-state">
                <NaveIcon name="lucide:wallet-minimal" />
                {t("billing.noFinancialAccount")}
              </div>
            ) : finance?.nextInvoice !== undefined ? (
              <article className="home-finance">
                <CardGlass variant="glass-sm" elevation={0} className="home-finance-amount">
                  <span className="home-finance-row">
                    <Typography as="span" variant="caption" tone="muted">
                      {t("billing.nextInvoice")}
                    </Typography>
                    <GlassChip variant={finance.overdueCount > 0 ? "destructive" : "neutral"}>
                      {finance.overdueCount > 0 ? t("billing.overdue") : t("billing.open")}
                    </GlassChip>
                  </span>
                  <Typography as="p" variant="display-sm" className="nave-amount-value">
                    {formatCurrency(finance.nextInvoice.amount.value, finance.nextInvoice.amount.currency)}
                  </Typography>
                  {finance.nextInvoice.dueAt !== undefined ? (
                    <span className="nave-meta-item">
                      <NaveIcon name="lucide:calendar-clock" />
                      {t("billing.due")}: {formatDate(finance.nextInvoice.dueAt)}
                    </span>
                  ) : null}
                </CardGlass>
                <ServiceIdentity name={finance.nextInvoice.serviceName || t("billing.invoice.label")} logoUrl={finance.nextInvoice.serviceLogoUrl} planName={finance.nextInvoice.planName} />
                {finance.nextInvoice.competence || finance.nextInvoice.billingPhase ? (
                  <span className="home-finance-row">
                    {finance.nextInvoice.competence ? <span className="nave-meta-item"><NaveIcon name="lucide:calendar-range" />{t("billing.competence")}: {formatMonth(finance.nextInvoice.competence)}</span> : null}
                    {finance.nextInvoice.billingPhase ? <GlassChip variant="purple">{t(`billing.phase.${finance.nextInvoice.billingPhase}`)}</GlassChip> : null}
                  </span>
                ) : null}
                <BlobCtaButton variant="primary" size="sm" onClick={() => navigate(`/billing/invoices/${finance.nextInvoice!.invoiceId}`)}>{t("billing.invoice.view")}</BlobCtaButton>
              </article>
            ) : (
              <div className="empty-state">
                <NaveIcon name="lucide:circle-check" />
                {t("billing.summary.clear")}
              </div>
            )}
          </SectionBoundary>
        ) : null}

        {shouldRenderHomeSection(home.relevantProductUsage) ? (
          <SectionBoundary
            icon="lucide:gauge"
            title={t("home.usage.title")}
            state={home.relevantProductUsage.state}
            emptyKey="home.usage.empty"
            className="home-span-5"
            viewAll={{ to: "/projects#products-heading", label: t("products.title") }}
          >
            <div className="usage-list">
              {home.relevantProductUsage.data?.products.map((product) => (
                <article className="usage-card" key={`${product.productKey}:${product.entitlementRef}`}>
                  <span className="home-finance-row">
                    <ServiceIdentity name={t(`products.name.${product.productKey}`)} logoUrl={product.logoUrl} />
                    {product.planRef ? <GlassChip variant="purple">{product.planRef.replace(/^common\.packageTier\./u, "").toUpperCase()}</GlassChip> : null}
                  </span>
                  {product.state === "UNAVAILABLE" ? <Typography as="p" variant="body-sm" tone="muted">{t(product.messageKey ?? "usage.unavailable")}</Typography> : product.metrics.map((metric) => (
                    <div className="nave-usage" key={metric.metricKey}>
                      <div className="nave-usage-head">
                        <span>{t(metric.labelKey)}</span>
                        <span className="nave-usage-value">
                          {formatQuantity(metric.used, metric.unit)}
                          {metric.included === undefined ? null : <small> / {formatQuantity(metric.included, metric.unit)}</small>}
                        </span>
                      </div>
                      {metric.included !== undefined && metric.included > 0 ? (
                        <Progress
                          value={Math.min(metric.used, metric.included)}
                          max={metric.included}
                          variant={metric.limitState === "HARD_LIMIT" ? "error" : metric.limitState === "SOFT_LIMIT" ? "warning" : "galaxy"}
                          size="sm"
                          aria-label={t(metric.labelKey)}
                        />
                      ) : null}
                    </div>
                  ))}
                </article>
              ))}
              {home.relevantProductUsage.data?.relevantAlerts.map((alert) => (
                <article className="usage-card" key={`${alert.productKey}:${alert.metricKey}`}>
                  <span className="home-finance-row">
                    <GlassChip variant="purple">
                      {t(`products.name.${alert.productKey}`)}
                    </GlassChip>
                    <Typography as="span" variant="caption" tone="muted">
                      {t(`usage.threshold.${alert.thresholdKind}`)}
                    </Typography>
                  </span>
                  <Typography as="strong" variant="body-sm">
                    {t(alert.labelKey)}
                  </Typography>
                  <Typography as="div" variant="title-lg" className="nave-usage-value">
                    {formatQuantity(alert.used, alert.unit)}
                  </Typography>
                  <Progress
                    value={alert.used}
                    max={alert.threshold}
                    variant={alert.thresholdKind === "HARD_LIMIT" ? "error" : "warning"}
                    size="sm"
                    aria-label={t(`usage.threshold.${alert.thresholdKind}`)}
                  />
                </article>
              ))}
            </div>
          </SectionBoundary>
        ) : null}
        {shouldRenderHomeSection(home.recentActivity) ? (
          <SectionBoundary
            icon="lucide:activity"
            title={t("home.activity.title")}
            state={home.recentActivity.state}
            emptyKey="home.activity.empty"
            className="home-span-7"
          >
            <RowList label={t("home.activity.title")}>
              {home.recentActivity.data?.items.map((activity) => {
                const event = activity.titleKey !== undefined ? t(activity.titleKey) : activity.titleText ?? t("activity.generic");
                // "Telescup" + plan "Telescup START" reads as one name with its tier, never twice.
                const name = activity.planName && activity.serviceName && activity.planName.startsWith(activity.serviceName) ? activity.planName : activity.serviceName;
                const content = {
                  leading: activity.serviceLogoUrl ? <img src={activity.serviceLogoUrl} alt="" /> : <RowIcon name={activityIcon(activity.activityType, activity.amount !== undefined)} tone="success" />,
                  title: name ? <PackageLabel text={name} /> : event,
                  subtitle: name ? [event, activity.billingPhase ? t(`billing.phase.${activity.billingPhase}`) : undefined].filter(Boolean).join(" · ") : undefined,
                  meta: (
                    <span className="nave-meta-item">
                      <NaveIcon name="lucide:calendar" />
                      <time dateTime={activity.occurredAt}>{formatDate(activity.occurredAt)}</time>
                      {activity.paymentMethod ? ` · ${t(`billing.method.${activity.paymentMethod}`)}` : ""}
                    </span>
                  ),
                  trailing: activity.amount && activity.currency ? <span className="nave-row-amount">{formatCurrency(activity.amount, activity.currency)}</span> : undefined,
                };
                return (
                  <li key={activity.activityId}>
                    {activity.target?.type === "INVOICE" ? (
                      <RowLink href={`/billing/invoices/${activity.target.invoiceId}`} {...content} />
                    ) : (
                      <div className="nave-row nave-row-static">
                        <span className="nave-row-leading">{content.leading}</span>
                        <span className="nave-row-main">
                          <span className="nave-row-title">{content.title}</span>
                          {content.subtitle ? <span className="nave-row-subtitle">{content.subtitle}</span> : null}
                          <span className="nave-row-meta">{content.meta}</span>
                        </span>
                        {content.trailing ? <span className="nave-row-trailing">{content.trailing}</span> : null}
                      </div>
                    )}
                  </li>
                );
              })}
            </RowList>
          </SectionBoundary>
        ) : null}

        {shouldRenderHomeSection(home.supportSummary) ? (
          <SectionBoundary
            icon="lucide:life-buoy"
            title={t("home.support.title")}
            state={home.supportSummary.state}
            emptyKey="home.support.empty"
            className="home-span-12"
            viewAll={{ to: "/support", label: t("common.viewAll") }}
          >
            <RowList label={t("home.support.title")}>
              {support?.items.map((ticket) => (
                <li key={ticket.ticketId}>
                  <RowLink
                    href={`/support/tickets/${ticket.ticketId}`}
                    leading={<RowIcon name="lucide:message-circle" tone={ticket.needsCustomerAction ? "alert" : "neutral"} />}
                    title={ticket.subject}
                    subtitle={t(`support.status.${ticket.customerStatus}`)}
                    meta={<span className="nave-meta-item"><NaveIcon name="lucide:clock" />{formatDate(ticket.lastActivityAt)}</span>}
                    trailing={ticket.unreadCount !== undefined && ticket.unreadCount > 0 ? (
                      <span className="nave-count" aria-label={t("support.unread")}>{ticket.unreadCount}</span>
                    ) : undefined}
                  />
                </li>
              ))}
            </RowList>
          </SectionBoundary>
        ) : null}

      </div>
    </div>
  );
};

const attentionCta = (path: string): string => {
  if (path.startsWith("/approvals")) return "home.attention.cta.review";
  if (path.startsWith("/decisions")) return "home.attention.cta.choose";
  if (path.startsWith("/requests") || path.startsWith("/questionnaires")) return "home.attention.cta.respond";
  return "home.attention.cta.open";
};

const AttentionLink = ({ href, children }: { href: string; children: ReactNode }) => (
  <Link className="attention-item" to={href}>{children}</Link>
);

const activityIcon = (activityType: string, payment: boolean): string => {
  if (payment || /PAYMENT|INVOICE/u.test(activityType)) return "lucide:banknote";
  if (/APPROVAL/u.test(activityType)) return "lucide:badge-check";
  if (/SUPPORT|TICKET/u.test(activityType)) return "lucide:message-circle";
  if (/PROJECT|STAGE|MILESTONE/u.test(activityType)) return "lucide:flag";
  if (/DOCUMENT|CONTRACT|PROPOSAL/u.test(activityType)) return "lucide:file-text";
  return "lucide:sparkles";
};

const healthVariant = (
  state: CustomerHomeReadModel["meta"]["state"],
):
  | "success"
  | "warning"
  | "destructive"
  | "neutral" => {
  if (state === "READY") {
    return "success";
  }

  if (
    state === "PARTIAL" ||
    state === "STALE"
  ) {
    return "warning";
  }

  if (state === "UNAVAILABLE") {
    return "destructive";
  }

  return "neutral";
};

const projectStatus = (
  status: string,
  t: (key: string) => string,
): string => {
  return t(`projects.status.${status}`);
};

const errorKeys = (
  error: HomeLoadErrorKind,
): Readonly<{
  title: string;
  body: string;
}> => {
  switch (error) {
    case "FORBIDDEN":
      return {
        title: "errors.forbidden.title",
        body: "errors.forbidden.body",
      };

    case "NOT_FOUND":
      return {
        title: "errors.notFound.title",
        body: "errors.notFound.body",
      };

    case "DEPENDENCY_UNAVAILABLE":
      return {
        title: "errors.unavailable.title",
        body: "errors.unavailable.body",
      };

    case "GENERIC":
      return {
        title: "errors.generic.title",
        body: "errors.generic.body",
      };

    default:
      return assertNever(error);
  }
};

const errorCode = (
  error: HomeLoadErrorKind,
): "403" | "404" | "503" | "500" => {
  switch (error) {
    case "FORBIDDEN":
      return "403";

    case "NOT_FOUND":
      return "404";

    case "DEPENDENCY_UNAVAILABLE":
      return "503";

    case "GENERIC":
      return "500";

    default:
      return assertNever(error);
  }
};

const assertNever = (
  value: never,
): never => {
  throw new Error(
    `Unhandled HomeLoadErrorKind: ${String(value)}`,
  );
};
